import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DateFilterFn,
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { merge, Subject, takeUntil } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MatDateFormats,
  MAT_NATIVE_DATE_FORMATS,
  DateAdapter,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import { CommonModule } from '@angular/common';
// eslint-disable-next-line no-duplicate-imports
import { NativeDateAdapter } from '@angular/material/core';

export const SEP_DATE_FORMAT: MatDateFormats = {
  parse: {
    dateInput: {
      year: 'numeric',
      month: 'long',
      day: undefined,
    } as Intl.DateTimeFormatOptions,
  },
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: {
      year: 'numeric',
      month: 'long',
      day: undefined,
    } as Intl.DateTimeFormatOptions,
  },
};

export const SEP_DATE_FORMAT_WITH_DAY: MatDateFormats = {
  parse: {
    dateInput: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions,
  },
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions,
  },
};

class CustomDateAdapter extends NativeDateAdapter {
  private readonly translocoService: TranslocoService;
  private enableDaySelection = false;

  constructor(matDateLocale: string, translocoService: TranslocoService) {
    super(matDateLocale);
    this.translocoService = translocoService;
  }

  setDaySelection(enabled: boolean): void {
    this.enableDaySelection = enabled;
  }

  override format(
    date: Date,
    _displayFormat: Intl.DateTimeFormatOptions,
  ): string {
    if (!this.enableDaySelection) {
      return new Intl.DateTimeFormat(this.locale as string, {
        year: 'numeric',
        month: 'long',
      }).format(date);
    }
    if (this.isToday(date)) {
      return this.translocoService.translate('general.today');
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }
}

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.css',
  providers: [
    {
      provide: DateAdapter,
      useFactory: (translocoService: TranslocoService) =>
        new CustomDateAdapter('en-US', translocoService),
      deps: [TranslocoService],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: SEP_DATE_FORMAT_WITH_DAY,
    },
  ],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    MatIcon,
    MatError,
    MatTooltip,
    CommonModule,
  ],
})
export class DatepickerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) control: FormControl<Date | null>;
  @Input() label?: string;
  @Input() placeholder = '';
  @Input() helpText = '';
  @Input() minDate: Date | null;
  @Input() maxDate: Date | null;
  @Input() allowedMonths: number[] | null;
  @Input() bottomMargin = true;
  @Input() id = '';
  @Input() hideErrors = false;
  @Input() enableDaySelection = false;

  private readonly destroy$: Subject<void> = new Subject();

  errorMessage = signal('');

  private readonly translocoService = inject(TranslocoService);
  private readonly dateAdapter = inject(DateAdapter<Date>) as CustomDateAdapter;

  constructor() {
    this.setLocale();
  }

  private setLocale() {
    const currentLang = this.translocoService.getActiveLang();
    this.dateAdapter.setLocale(currentLang);
  }

  ngOnInit() {
    if (this.dateAdapter instanceof CustomDateAdapter) {
      this.dateAdapter.setDaySelection(this.enableDaySelection);
    }

    merge(
      this.control.statusChanges
        .pipe(startWith(this.control.status))
        .pipe(takeUntil(this.destroy$)),
      this.control.valueChanges
        .pipe(startWith(this.control.value))
        .pipe(takeUntil(this.destroy$)),
    ).subscribe(this.updateErrorMessage.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateErrorMessage() {
    const { errors } = this.control;
    if (errors) {
      const [firstErrorKey] = Object.keys(errors);
      this.errorMessage.set(firstErrorKey || 'default');
    } else {
      this.errorMessage.set('');
    }
  }

  setMonthAndYear(
    normalizedMonthAndYear: Date,
    datepicker: MatDatepicker<Date>,
  ) {
    if (this.enableDaySelection) {
      return;
    }
    const ctrlValue = this.control.value ?? new Date();
    ctrlValue.setFullYear(
      normalizedMonthAndYear.getFullYear(),
      normalizedMonthAndYear.getMonth(),
      1,
    );
    ctrlValue.setHours(12, 0, 0, 0);
    this.control.setValue(ctrlValue);
    datepicker.close();
  }

  dateFilter: DateFilterFn<Date | null> = (date) => {
    if (!date) {
      return false;
    }
    if (!this.allowedMonths) {
      return true;
    }
    return this.allowedMonths.includes(date.getMonth());
  };
}
